import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../redux/network/https";
import { endPoints, queryKeys } from "../../redux/network/constant";

const fetchProfileDetails = async () => {
  const { data } = await axiosInstance.get(endPoints.GetProfileDetails);
  return data?.data;
};

const putProfileDetails = async (payload) => {
  const { data } = await axiosInstance.put(
    endPoints.UpdateProfileDetail,
    payload,
  );
  return data?.data;
};

const postProfilePhoto = async (formData) => {
  const { data } = await axiosInstance.post(endPoints.uploadProfileImage, formData);
  return data?.data;
};

const fetchProfileCompletion = async () => {
  const { data } = await axiosInstance.get(endPoints.profileCompletion);
  return data?.data;
};

export const useProfileCompletion = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.ProfileCompletion(),
    queryFn: fetchProfileCompletion,
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
};

export const useProfileDetails = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.ProfileDetails(),
    queryFn: fetchProfileDetails,
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    gcTime: 10 * 60 * 1000,   // keep in cache for 10 minutes
  });
};

export const useUpdateProfileDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: putProfileDetails,
    onSuccess: (data, variables) => {
      if (
        variables &&
        Object.prototype.hasOwnProperty.call(variables, "client_mode_enabled")
      ) {
        queryClient.setQueryData(queryKeys.ProfileDetails(), (current) =>
          current
            ? {
                ...current,
                client_mode_enabled: variables.client_mode_enabled,
              }
            : current,
        );
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.ProfileDetails() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProfileCompletion(),
      });
    },
  });
};

export const useUpdateProfilePhoto = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postProfilePhoto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ProfileDetails() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProfileCompletion(),
      });
    },
  });
};

const postCreateVerificationSession = async () => {
  const { data } = await axiosInstance.post(
    endPoints.CreateVerificationSession,
  );
  return data?.data;
};

export const useCreateVerificationSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postCreateVerificationSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ProfileDetails() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProfileCompletion(),
      });
    },
  });
};

const fetchPortfolioImages = async () => {
  const { data } = await axiosInstance.get(endPoints.PortfolioImages);
  return data?.data;
};

export const useGetPortfolioImages = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.PortfolioImages(),
    queryFn: fetchPortfolioImages,
    enabled,
  });
};

const postPortfolioImages = async (formData) => {
  const { data } = await axiosInstance.post(
    endPoints.PortfolioImages,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return data?.data;
};

export const useUploadPortfolioImages = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postPortfolioImages,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.PortfolioImages() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProfileCompletion(),
      });
    },
  });
};

const deletePortfolioImage = async (imageId) => {
  const { data } = await axiosInstance.delete(
    `${endPoints.PortfolioImages}${imageId}/`,
  );
  return data?.data;
};

export const useDeletePortfolioImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePortfolioImage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.PortfolioImages() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProfileCompletion(),
      });
    },
  });
};

const fetchCities = async (stateId) => {
  const { data } = await axiosInstance.post(endPoints.States, { id: stateId });
  return data?.data;
};

export const useCities = (stateId, enabled = true) => {
  const hasValidStateId = !!(stateId && stateId !== "" && !Array.isArray(stateId));
  const isEnabled = !!(enabled && hasValidStateId);
  return useQuery({
    queryKey: queryKeys.Cities(stateId),
    queryFn: () => fetchCities(stateId),
    enabled: isEnabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};
